import { FACADE_COLORS, FACADE_PATTERNS, FACADES, handleForFacade } from "../catalog";
import type { DoorFacade, FacadeType, HandleType, WardrobeModule } from "../types";
import { decorPriceMaterial } from "./decors";
import { eggerDecor } from "./eggerDecors";
import { facadeSupportsMilling, normalizeFacadeColorCode, normalizeFacadeColorHex } from "./facades";
import { hingedDoorLeafLayout, mezzanineFlapLayout } from "./moduleRules";

/**
 * Свой фасад у отдельной створки распашного шкафа (приёмка 16.10): например,
 * у углового одна дверь стеклянная, другая глухая. У створки те же поля, что
 * у фасада шкафа; нет своих — створка как шкаф. Ручка общая, но подбирается к
 * фасаду створки: у стекла — зацеп в профиле, на ЛДСП фрезерованной нет.
 */

export const DOOR_FACADE_KEYS = [
  "facade", "facadePattern", "facadeMaterial", "facadeDecor",
  "facadeColor", "facadeColorHex", "facadeColorCode", "facadePaintSides"
] as const;

export const supportsDoorFacades = (module: WardrobeModule) =>
  module.door === "hinged" &&
  // У углового одна дверь (28.10) — её фасад и есть фасад шкафа.
  (module.type === "single" || module.type === "double" || module.type === "mezzanine") &&
  !mezzanineFlapLayout(module);

/** Створки, у которых может быть свой фасад, — столько же, сколько строит 3D. */
export const doorFacadeLeafCount = (module: WardrobeModule) =>
  supportsDoorFacades(module) ? hingedDoorLeafLayout(module).length : 0;

/** Отделка шкафа — то, что у створки «как у шкафа». */
export const moduleDoorFacade = (module: WardrobeModule): DoorFacade => {
  const finish: DoorFacade = { facade: module.facade };
  DOOR_FACADE_KEYS.forEach((key) => {
    if (key !== "facade" && module[key] !== undefined) (finish as unknown as Record<string, unknown>)[key] = module[key];
  });
  return finish;
};

/** Привести отделку створки к правилам фасада шкафа; мусор — null. */
export const normalizeDoorFacade = (value: unknown): DoorFacade | null => {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;
  const raw = source.facade;
  if (typeof raw !== "string" || !(raw in FACADES)) return null;
  // Старый «глянец» — это МДФ в эмали с рисунком, как у шкафа.
  const facade: FacadeType = raw === "gloss" ? "satin" : raw as FacadeType;
  const result: DoorFacade = { facade };
  const pattern = source.facadePattern;
  if (facadeSupportsMilling(facade) && typeof pattern === "string" && pattern in FACADE_PATTERNS && pattern !== "plain") {
    result.facadePattern = pattern as DoorFacade["facadePattern"];
  }
  if (facade === "wood") {
    const decor = eggerDecor(source.facadeDecor);
    if (decor) {
      result.facadeDecor = decor.code;
      result.facadeMaterial = decorPriceMaterial(decor.code);
    }
  }
  if (facade === "satin") {
    if (typeof source.facadeColor === "string" && source.facadeColor in FACADE_COLORS) {
      result.facadeColor = source.facadeColor as DoorFacade["facadeColor"];
    }
    const hex = normalizeFacadeColorHex(source.facadeColorHex);
    if (hex) result.facadeColorHex = hex;
    const code = normalizeFacadeColorCode(source.facadeColorCode);
    if (code) result.facadeColorCode = code;
    if (source.facadePaintSides === "two") result.facadePaintSides = "two";
  }
  return result;
};

/** Список своих фасадов по числу створок; нет ни одного — undefined. */
export const normalizeDoorFacades = (module: WardrobeModule): WardrobeModule["doorFacades"] => {
  const count = doorFacadeLeafCount(module);
  if (!count || !Array.isArray(module.doorFacades)) return undefined;
  const list = Array.from({ length: count }, (_, index) => normalizeDoorFacade(module.doorFacades![index]));
  while (list.length && !list[list.length - 1]) list.pop();
  return list.length ? list : undefined;
};

export const doorFacadeOverride = (module: WardrobeModule, index: number): DoorFacade | null =>
  supportsDoorFacades(module) ? normalizeDoorFacade(module.doorFacades?.[index]) : null;

export const hasDoorFacades = (module: WardrobeModule) =>
  doorFacadeLeafCount(module) > 0 && (module.doorFacades ?? []).some((_, index) => doorFacadeOverride(module, index));

/** Шкаф с отделкой этой створки: для 3D, цены и описаний. */
export const leafFinish = (module: WardrobeModule, index: number): WardrobeModule => {
  const override = doorFacadeOverride(module, index);
  if (!override) return module;
  const merged: WardrobeModule = { ...module };
  DOOR_FACADE_KEYS.forEach((key) => { delete (merged as unknown as Record<string, unknown>)[key]; });
  return { ...merged, ...override };
};

/** Ручка створки: общая, но допустимая для её фасада. */
export const leafHandle = (module: WardrobeModule, index: number): HandleType =>
  handleForFacade(module.handle, leafFinish(module, index).facade);

/** Поставить или снять свой фасад створки; вернуть новый список для updateModule. */
export const withDoorFacade = (module: WardrobeModule, index: number, finish: DoorFacade | null) => {
  const count = doorFacadeLeafCount(module);
  const list = Array.from({ length: count }, (_, leaf) => leaf === index ? normalizeDoorFacade(finish) : doorFacadeOverride(module, leaf));
  while (list.length && !list[list.length - 1]) list.pop();
  return list.length ? list : undefined;
};

/** Название створки для выбора и техкарты: у углового одна дверь (28.10). */
export const doorLeafName = (module: WardrobeModule, index: number) =>
  module.type === "corner45" ? "Дверь углового" : `Дверь ${index + 1}`;
